import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import GoogleReviewsCarousel from '../components/GoogleReviewsCarousel';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  Phone, CheckCircle, Clock, Shield, Star, Users, 
  Wrench, Droplets, Flame, Wind, ArrowRight,
  Award, ThermometerSun, Zap
} from 'lucide-react';


const HomePage = () => {
  const navigate = useNavigate();

const handleLearnMore = (sectionId) => {
  navigate(`/services#${sectionId}`);
};
 const services = [
  {
    icon: Droplets,
    title: 'Plumbing Services',
    description: 'Complete plumbing solutions for every need, from quick fixes to new installations.',
    features: ['24/7 Emergency Repairs', 'Drain Cleaning & Unclogging', 'Water Heater Repair & Installation', 'Pipe Repairs & New Pipe Installation'],
    sectionId: 'plumbing-services'
  },
  {
    icon: Flame,
    title: 'Heating Services',
    description: 'Expert heating system installation, maintenance, and repair to keep you warm all winter long.',
    features: ['Boiler Repair & Replacement', 'Oil-to-Gas Conversions', 'Emergency Heating Service', 'Seasonal System Maintenance & Tune-Ups'],
    sectionId: 'heating-services'
  },
  {
    icon: Wind,
    title: 'Air Conditioning',
    description: 'Stay cool with professional AC solutions designed for comfort and efficiency.',
    features: ['New AC Installation & Replacement', 'Fast Emergency AC Repair', 'Scheduled Maintenance Plans', 'High-Efficiency Cooling System Upgrades'],
    sectionId: 'ac-services'
  }
];
  const whyChooseUs = [
    { icon: Clock, title: '24/7 Emergency Service', description: 'Available around the clock for urgent repairs' },
    { icon: Shield, title: 'Licensed & Insured', description: 'Fully licensed and insured for your protection' },
    { icon: Award, title: '20+ Years Experience', description: ' decades of reliable service in Morris County' },
    { icon: Users, title: 'Expert Technicians', description: 'Highly trained and certified professionals' },
    { icon: CheckCircle, title: 'Satisfaction Guaranteed', description: 'We stand behind our work' },
    { icon: ThermometerSun, title: 'Energy Efficient', description: 'Solutions that save you money on utility bills' }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      location: 'Budd Lake, NJ',
      rating: 5,
      text: 'Mount Olive Plumbing saved the day when our water heater failed. Professional, quick, and reasonably priced!'
    },
    {
      name: 'Mike Peterson',
      location: 'Hackettstown, NJ',
      rating: 5,
      text: 'Excellent HVAC installation. The team was professional and the work quality exceeded expectations.'
    },
    {
      name: 'Lisa Chen',
      location: 'Mount Olive, NJ',
      rating: 5,
      text: 'Emergency plumbing service at 2 AM - they were there within an hour. Highly recommend!'
    }
  ];

  return (
    
    <div className="min-h-screen bg-white">
      <Helmet>
  <title>Mount Olive Plumbing, Heating & AC | 24/7 Service in Morris County</title>
  <meta name="description" content="24/7 emergency plumbing, heating & AC service in Mount Olive & Budd Lake, NJ. Licensed, insured & trusted across Morris County." />
  
  {/* Open Graph Tags */}
  <meta property="og:title" content="Mount Olive Plumbing, Heating & AC" />
  <meta property="og:description" content="24/7 emergency plumbing, heating & cooling services in Mount Olive, NJ. Licensed, insured, and trusted." />
  <meta property="og:url" content="https://www.mtoliveplumbing.com/" />
  <meta property="og:type" content="website" />
  
  {/* Additional SEO Meta Tags */}
  <meta name="keywords" content="plumbing Mount Olive NJ, heating repair Budd Lake, AC service Morris County, emergency plumber, HVAC installation" />
  <link rel="canonical" href="https://www.mtoliveplumbing.com/" />
</Helmet>
             {/* Hero Section */}
       <section className="relative py-20 sm:py-24 md:py-32 flex items-center justify-center overflow-hidden">
        {/* Background Image with Gradient Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/HomePage/heroHomePage.webp"
            alt="Hero Background"
            className="w-full h-full object-cover"
          />
          
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/75 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 md:pt-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
            {/* Hero Content */}
            <motion.div
              className="text-white space-y-6 sm:space-y-8"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
                             <div
                 className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-semibold border-2 border-white/20 text-xs sm:text-sm md:text-[15px]"
               >
                 <img src="/images/HomePage/EmergencyIconHomePage.png" alt="Emergency" className="h-3 w-3 sm:h-4 sm:w-4" />
                 <span>24/7 Emergency Service for Homes and Businesses</span>
               </div>

                               <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[64px] font-bold leading-tight max-w-4xl">
                  <span className="text-white">Trusted </span>
                  <span className="text-[#45C1FF]">Plumbing Heating & Cooling</span>
                  <span className="text-white"> Experts</span>
                </h1>

               <p className="text-base sm:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-2xl">
                 Reliable plumbing, heating, and AC solutions serving Budd Lake and surrounding Morris County for 20+ years. Licensed, insured, and ready to respond day or night.
               </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a
                  href="tel:+1-973-862-0606"
                  className="inline-flex items-center justify-center space-x-3 bg-white text-[#A51E29] px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Phone className="h-5 w-5 sm:h-6 sm:w-6" />
                  <span>Call Now: (973) 862-0606</span>
                </motion.a>

                {/* <motion.button
                  className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold border-2 border-white/20 hover:bg-white/20 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Get Free Quote</span>
                  <ArrowRight className="h-5 w-5" />
                </motion.button> */}
              </div>

                             <div className="grid grid-cols-3 gap-4 sm:gap-6 lg:gap-8 pt-6 sm:pt-8">
                 {[
                   { number: '20+', label: 'Years Experience' },
                   { number: '5000+', label: 'Happy Customers' },
                   { number: '24/7', label: 'Emergency Service' }
                 ].map((stat, index) => (
                   <motion.div
                     key={index}
                     className="text-center"
                     initial={{ opacity: 0, y: 20 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ delay: 0.5 + index * 0.1 }}
                   >
                     <div className="text-2xl sm:text-3xl font-bold text-[#45C1FF]">{stat.number}</div>
                     <div className="text-white text-xs sm:text-sm font-medium">{stat.label}</div>
                   </motion.div>
                 ))}
               </div>
            </motion.div>

            {/* Emergency Service Card */}
            <motion.div
              className="lg:justify-self-end"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="bg-white/95 backdrop-blur-lg p-6 sm:p-8 rounded-3xl shadow-2xl max-w-md">
                <div className="text-center space-y-4 sm:space-y-6">
                  <div className="bg-gradient-to-br from-[#A51E29] to-[#A51E29] p-3 sm:p-4 rounded-2xl w-fit mx-auto">
                    <Clock className="h-10 w-10 sm:h-12 sm:w-12 text-white" />
                  </div>
                  
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Emergency Service</h3>
                    <p className="text-sm sm:text-base text-gray-600">Plumbing emergency? We're here 24/7 to help!</p>
                  </div>

                                     <motion.a
                     href="tel:+1-973-862-0606"
                     className="block bg-gradient-to-r from-[#A51E29] to-[#A51E29] text-white py-3 sm:py-4 px-6 rounded-2xl font-bold text-lg sm:text-xl shadow-lg hover:shadow-xl transition-all duration-300"
                     whileHover={{ scale: 1.02 }}
                     whileTap={{ scale: 0.98 }}
                   >
                     Call Emergency Line
                   </motion.a>

                  <div className="flex items-center justify-center space-x-3 sm:space-x-4 text-xs sm:text-sm text-gray-500">
                    <div className="flex items-center space-x-1">
                      <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />
                      <span>Licensed</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />
                      <span>Insured</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />
                      <span>Bonded</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

             {/* Services Section */}
       <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
         <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-8 sm:mb-12 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Our Professional Services
            </h2>
                                     <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-5xl mx-auto px-4">
              From urgent repairs to complete system installations, we provide trusted <strong>plumbing, heating, and air conditioning solutions</strong> for homes and businesses across Morris County. Our licensed technicians deliver fast, reliable service backed by over 20 years of local expertise.
            </p>
          </motion.div>

                                           <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {services.map((service, index) => (
                                                                                    <motion.div
                   key={index}
                   className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 group overflow-hidden flex flex-col h-full"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                >
                                                                                              {/* Service Image */}
                     <div className="relative mb-4 sm:mb-6 overflow-hidden rounded-md">
                       <img 
                         src={`/images/HomePage/${service.title === 'Plumbing Services' ? 'PlumbingHomePage.webp' : 
                               service.title === 'Heating Services' ? 'heatingHomePage.webp' : 
                               'fansHomePage.webp'}`}
                         alt={service.title}
                         className={`w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-500 ${service.title === 'Air Conditioning' ? 'object-center' : ''}`}
                       />
                       <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                     </div>
                  
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">{service.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">{service.description}</p>
                  
                  <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8 flex-grow">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2 sm:space-x-3">
                        <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 flex-shrink-0" />
                        <span className="text-sm sm:text-base text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                <motion.button
  onClick={() => handleLearnMore(service.sectionId)}
  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2.5 sm:py-3 px-6 rounded-2xl font-semibold hover:shadow-lg transition-all duration-300 mt-auto cursor-pointer text-sm sm:text-base"
  whileHover={{ scale: 1.02 }}
  whileTap={{ scale: 0.98 }}
>
  Learn More
</motion.button>
                </motion.div>
             ))}
           </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-8 sm:mb-12 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Why Choose Mount Olive Plumbing?
            </h2>
                         <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
               With over <b>20</b> years of experience serving Morris County, we've built our reputation on quality work, honest pricing, and exceptional customer service.
             </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                className="text-center p-4 sm:p-6 rounded-2xl hover:bg-blue-50 transition-all duration-300 group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 sm:p-4 rounded-2xl w-fit mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                  <item.icon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{item.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

     {/* Testimonials Section */}
{/* Testimonials Section */}
<GoogleReviewsCarousel />

      {/* CTA Section */}
      <section className="relative py-12 sm:py-16 md:py-20 text-white overflow-hidden">
        {/* Background Image with Gradient Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/HomePage/CTAHomePage.webp"
            alt="Construction and Roadwork Scene"
            className="w-full h-full object-cover object-top opacity-12"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2462E9]/90 to-[#1E40AF]/90"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className="space-y-6 sm:space-y-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
                                       {/* First Text Container */}
              <div className="mb-4 sm:mb-6">
                <h3 className="text-xs sm:text-sm md:text-[15px] font-bold text-white mb-4 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 inline-block">
                  Plumbing & HVAC estimate in Morris County
                </h3>
              </div>

             {/* Second Text Container */}
             <div className="mb-4 sm:mb-6">
               <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[70px] font-bold text-white mb-4">
                 Get Your Free <span className="text-[#45C1FF]">Plumbing & HVAC</span> Estimate Today
               </h2>
             </div>

             {/* Third Text Container */}
             <div className="mb-4 sm:mb-6">
               <p className="text-base sm:text-lg md:text-xl lg:text-[24px] font-bold text-white">
                 Need immediate help or planning a new installation?
               </p>
             </div>

             {/* Fourth Text Container */}
             <div className="mb-6 sm:mb-8">
               <p className="text-sm sm:text-base md:text-lg lg:text-[20px] font-normal text-white max-w-4xl mx-auto leading-relaxed px-4">
                 Call Mount Olive Plumbing now or request your free, no-obligation estimate. Our licensed team provides expert plumbing, heating, and air conditioning solutions day or night.
               </p>
             </div>
            
            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
                             <motion.a
                 href="tel:+1-973-862-0606"
                 className="inline-flex items-center justify-center space-x-3 bg-white text-blue-700 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg shadow-2xl hover:shadow-white/25 transition-all duration-300"
                 whileHover={{ scale: 1.05 }}
                 whileTap={{ scale: 0.95 }}
               >
                 <Phone className="h-5 w-5 sm:h-6 sm:w-6" />
                 <span>Call: (973) 862-0606</span>
               </motion.a>
              
              {/* <motion.button
                className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold border-2 border-white/20 hover:bg-white/20 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Get Free Estimate</span>
                <ArrowRight className="h-5 w-5" />
              </motion.button> */}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;